import { type ReactNode, useRef, useState } from 'react';
import { cn } from 'v5/platform/dom/cn';
import { MediaBackdrop } from './MediaBackdrop';
import {
  MediaLayer,
  useMediaLayerHold,
  useMediaLayerStack
} from './MediaLayers';
import { usePictureFit } from './usePictureFit';

/**
 * A picture's layers (`MediaLayers`), one per picture shown: the next fades in once loaded, the
 * one before dims meanwhile with an aurora over it. `under` is the ground of one that doesn't
 * fill its box (`MediaBackdrop`).
 */
export function PictureLayers({
  subject,
  under,
  instant,
  children
}: {
  subject: string;
  /** Under the picture, never dimmed: the ground of one that doesn't fill the box (`MediaBackdrop`). */
  under?: ReactNode;
  /** Shown at once, as `MediaLayer`'s `instant`. */
  instant?: boolean;
  children: ReactNode;
}) {
  const stack = useMediaLayerStack({
    subject,
    under,
    content: children,
    createData: () => undefined
  });
  return stack.layers.map(layer => (
    <MediaLayer
      key={layer.id}
      layer={layer}
      onReady={() => stack.onReady(layer.id)}
      onSettled={() => stack.onSettled(layer.id)}
      className="bg-bg-subtle"
      instant={instant}
      // the picture fills the card: the wait's aurora lies over it, lit as in the dialog
      auroraOverContent
      auroraClassName="mix-blend-screen text-white [--fx-intensity:.2]"
      contentClassName="absolute inset-0"
    />
  ));
}

/**
 * A picture its layer waits for; when the resized copy fails (Cloudinary can't fetch it), the
 * original once, as the media dialog does. It fills its box (cropped) when it is at least as large
 * as the box; a smaller one is never enlarged: it shows whole, centred on its blurred copy
 * (`MediaBackdrop`), its corners as round as the ground beside it allows (`cornerRadiusOf`).
 */
export function LayerPicture({
  src,
  fallbackSrc,
  contained = false,
  onFailed
}: {
  src: string;
  fallbackSrc?: string;
  /** The whole picture, centred, never past its own size (on a backdrop that fills the rest). */
  contained?: boolean;
  /** Neither the picture nor its fallback loaded. */
  onFailed?(): void;
}) {
  const release = useMediaLayerHold();
  // the picture it fell back to, kept for the src it fell back from: a new src starts over
  const [fellBack, setFellBack] = useState<{ from: string; to: string } | null>(
    null
  );
  const shown = fellBack?.from === src ? fellBack.to : src;
  const box = useRef<HTMLDivElement>(null);
  const { fit, radius, measure } = usePictureFit(box);
  const whole = contained || (fit !== undefined && fit !== 'covers');
  const picture = (
    <img
      src={shown}
      alt=""
      // the picture itself, not its backdrop: what a host's hover zooms
      data-media-picture=""
      decoding="async"
      onLoad={event => {
        measure(event.currentTarget);
        release();
      }}
      onError={() => {
        if (fallbackSrc && shown !== fallbackSrc) {
          setFellBack({ from: src, to: fallbackSrc });
          return;
        }
        release();
        onFailed?.();
      }}
      className={cn(
        whole
          ? 'max-h-full max-w-full object-contain'
          : 'size-full object-cover',
        // over its own backdrop, its sibling
        whole && !contained && 'relative'
      )}
      // as round as the ground beside it allows, up to the media dialog's corners
      style={whole ? { borderRadius: radius } : undefined}
    />
  );
  return (
    <div
      ref={box}
      className="absolute inset-0 flex items-center justify-center"
    >
      {/* Only under one that doesn't fill the box. The host gives a contained one its ground. */}
      {!contained && whole && (
        <div className="absolute inset-0">
          <MediaBackdrop src={shown} />
        </div>
      )}
      {picture}
    </div>
  );
}
